'use client';

import { useCallback, useSyncExternalStore } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import type { ItemCrumb, RootCrumb } from './path-trail';

/**
 * The tap's mount (docs/LOADING.md §2, tool 3): the target a tapped row named,
 * held so the skeleton mounted by the tap can draw it before the router
 * commits the navigation.
 *
 * The router commits at the tap only when it already holds the target's shell
 * (`navigateToUnknownRoute`, Next 16.3.6); otherwise it awaits the server and
 * the old screen stands. So the row sets this store from its `Link`'s
 * `onNavigate` (Next calls it BEFORE the transition that dispatches the
 * navigation, so the store's update paints first), and `TapMount` in the shell
 * draws `skeletonFor(href)` while the pathname has not reached the target.
 * When the router commits, the route's `loading.tsx` draws the same skeleton
 * with the same title (the skeletons read this store too), so the handover
 * shows no change.
 *
 * A store and never a context value, for the reason `SlotStore` gives
 * (topbar-slot.tsx): a context above the pages that changed on a tap would
 * reach every streamed boundary that has not hydrated.
 *
 * The target stays set once the pathname arrives: the route's skeleton reads
 * the title from here until the content lands, and a later mount of the same
 * item's route reads the same item's title. A new tap overwrites it and a
 * dismissal clears it; once the pathname has left the page the tap was taken
 * on, it never stands again (`markTapLeft`).
 */

/** The trail the target will carry, as the tapped row knows it: the root it
 *  starts from and the items drilled through (`PathTrail`'s props). */
export interface TapTrail {
  root?: RootCrumb;
  items: ItemCrumb[];
}

export interface TapTarget {
  /** The row's href, as the router will navigate to it. */
  href: string;
  /** The row's title, drawn in the item skeleton's title seat. */
  title?: string;
  /** The target's trail, drawn in the top bar. */
  trail?: TapTrail;
  /** The pathname the tap was taken on. */
  from: string;
  /** When the tap was taken (`Date.now()`), for the 10 s limit. */
  at: number;
  /** The pathname has left the page the tap was taken on (it reached the
   *  target, or went somewhere else): the tap's mount never stands again,
   *  even when the person comes back. */
  left?: boolean;
}

let target: TapTarget | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getTapTarget(): TapTarget | null {
  return target;
}

/** Name the tapped row's target. Overwrites any earlier tap. */
export function setTapTarget(next: Omit<TapTarget, 'at' | 'from'>): void {
  target = { ...next, from: window.location.pathname, at: Date.now() };
  emit();
}

/**
 * Record that the pathname left the page the tap was taken on (`TapMount`).
 *
 * IN PLACE and without an emit, on purpose: this runs right after the router
 * commits, while the target's content is still streaming in a transition,
 * and a store that changes under a transition makes React finish that
 * transition as a blocking update, which hides the committed peek behind the
 * route's fallback (found by e2e/trail.spec.ts, 2026-09-29). Nothing on screen
 * changes with it: the tap's mount already stopped standing when the pathname
 * moved, and every reader re-reads it on the next pathname change.
 */
export function markTapLeft(): void {
  if (target !== null) target.left = true;
}

export function clearTapTarget(): void {
  if (target === null) return;
  target = null;
  emit();
}

/** The target's pathname: the href without its query or hash. */
export function pathnameOf(href: string): string {
  return new URL(href, 'http://x').pathname;
}

/** Whether the tap's mount stands over `pathname`: a target is named that
 *  the pathname has not reached yet, and the pathname is still the one the
 *  tap was taken on. */
export function standsOver(t: TapTarget | null, pathname: string): t is TapTarget {
  return t !== null && !t.left && t.from === pathname && pathnameOf(t.href) !== pathname;
}

/** The same question at event time, read from the live location (the router
 *  writes the URL when it commits, so this is the committed pathname). */
export function tapMountStands(): boolean {
  return standsOver(target, window.location.pathname);
}

export function useTapTarget(): TapTarget | null {
  return useSyncExternalStore(subscribe, getTapTarget, () => null);
}

/** Whether the tap's mount stands over the current route. */
export function useTapMountStanding(): boolean {
  return standsOver(useTapTarget(), usePathname());
}

/** The target, when the current route IS it: what the route's own skeleton
 *  draws, so the route's mount matches the tap's. */
export function useTapTargetHere(): TapTarget | null {
  const t = useTapTarget();
  const pathname = usePathname();
  return t !== null && pathnameOf(t.href) === pathname ? t : null;
}

/**
 * The dismissal: clears the target and navigates to the CURRENT url, which
 * the router's action queue takes as a navigation arriving while one is
 * pending; it marks the pending one discarded, and its state is never applied
 * (`dispatchAction`, next/dist/client/components/app-router-instance.js).
 * Never `history.back()`: history holds no entry for the target yet, so that
 * would leave the screen the person is on. Returns whether a tap's mount
 * stood, so a Back handler can fall through to its own act when none did.
 */
export function useDismissTapMount(): () => boolean {
  const router = useRouter();
  return useCallback(() => {
    if (!tapMountStands()) return false;
    clearTapTarget();
    const { pathname, search, hash } = window.location;
    router.replace(`${pathname}${search}${hash}`, { scroll: false });
    return true;
  }, [router]);
}
